import {
  DEFAULT_NUM_NEW_PRODUCTS,
  MAX_NEW_PRODUCTS_PER_RULE,
  type SourcingRuleFields,
} from "@loot-os/fulfillment-core/vendor-product-sourcing/types";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { useState } from "react";

import { ListingTagsPicker } from "./ListingTagsPicker";
import { TenantsPicker } from "./TenantsPicker";

/** A sourcing rule's editable fields, as the dialog collects them. */
export type SourcingRuleDraft = Pick<
  SourcingRuleFields,
  "url" | "numNewProducts" | "tags" | "tenantIds"
>;

const EMPTY: SourcingRuleDraft = {
  url: "",
  numNewProducts: DEFAULT_NUM_NEW_PRODUCTS,
  tags: [],
  tenantIds: [],
};

/**
 * Adds a sourcing rule, or edits one. The page URL is the rule's identity and is set once, at
 * create; to cover another page, add a rule for it. The caller owns `open`, so the dialog stays
 * open while the save runs and closes on success, and no control closes it mid-save. The form
 * starts from `rule` when it mounts, so the caller gives each opening its own `key`.
 */
export function SourcingRuleDialog({
  open,
  rule,
  isPending,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  /** The rule to edit; omitted to add one. */
  rule?: SourcingRuleDraft;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (draft: SourcingRuleDraft) => void;
}) {
  const [draft, setDraft] = useState<SourcingRuleDraft>(rule ?? EMPTY);
  const isEdit = rule !== undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sourcing rule" : "Add sourcing rule"}</DialogTitle>
          <DialogDescription>
            Each day the cron reads this page and lists the first products on it that are not listed
            yet, for each tenant chosen here. Choose no tenant to list them shared.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave(draft);
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="sourcing-url">StockX page URL</Label>
            <Input
              id="sourcing-url"
              value={draft.url}
              disabled={isEdit || isPending}
              placeholder="https://stockx.com/category/sneakers?sort=most-active"
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Stored exactly as entered: the query string is the page's sort and filter.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sourcing-count">Products per run</Label>
            <Input
              id="sourcing-count"
              type="number"
              min={1}
              max={MAX_NEW_PRODUCTS_PER_RULE}
              value={draft.numNewProducts}
              disabled={isPending}
              onChange={(e) => setDraft({ ...draft, numNewProducts: Number(e.target.value) })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sourcing-tags">Tags</Label>
            <ListingTagsPicker
              id="sourcing-tags"
              value={draft.tags}
              disabled={isPending}
              onChange={(tags) => setDraft({ ...draft, tags })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sourcing-tenants">Tenants</Label>
            <TenantsPicker
              id="sourcing-tenants"
              value={draft.tenantIds}
              disabled={isPending}
              onChange={(tenantIds) => setDraft({ ...draft, tenantIds })}
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground">
                A tenant added here is given every product this rule's other tenants list now,
                copied from their rows with no fetch.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || draft.url.trim() === ""}>
              {isPending ? "Saving…" : isEdit ? "Save rule" : "Add rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
